import type { ApiRequestContext } from "~/lib/apiClient";

/**
 * Module-level store for API request context (tenant, user profile).
 *
 * ## Why two request-context mechanisms exist
 *
 * 1. **This module stores** — for Orval-generated fetch functions that run
 *    outside React's component tree and cannot access React context. The
 *    `AuthProvider` effect calls `setRequestContext()` whenever the user or
 *    tenant changes; the Orval custom-fetch adapter reads it via
 *    `getRequestContext()` so generated code automatically receives tenant
 *    headers without per-call context parameters.
 *
 * 2. **`useApiRequestContext()` hook** — for manually written feature hooks
 *    that live inside the React tree and can read from `useAuth()` context.
 *
 * Both are kept in sync by the same `AuthProvider` effect in
 * `AuthContext.tsx`. Use the hook in React components/hooks; use
 * `getRequestContext()` only in non-React code paths (e.g., Orval custom
 * fetch adapters).
 */
let currentContext: ApiRequestContext | undefined;

export const setRequestContext = (ctx: ApiRequestContext | undefined): void => {
  currentContext = ctx;
};

export const getRequestContext = (): ApiRequestContext | undefined => {
  return currentContext;
};
